import React, { createContext, useState, useContext, ReactNode } from 'react';
import DealerGroup from '../apiService/dealerGroup/dealerGroupApiService';
import { useQueryClient } from '@tanstack/react-query';

// ----------- Type Definitions -----------
interface DealerGroupContextType {
  loading: boolean;
  error: string;
  isEditing: boolean;

  dealerGroups: any[];
  dealerGroup: any;
  selectedDealerGroup: any | null;

  setError: React.Dispatch<React.SetStateAction<string>>;
  setDealerGroup: React.Dispatch<React.SetStateAction<any>>;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;

  addDealerGroup: (dealerGroup: any, callback?: () => void) => Promise<void>;
  deleteDealerGroup: (
    dealerGroupId: string,
    type: string,
    callback?: () => void,
  ) => Promise<void>;
  editDealerGroup: (
    dealerGroupId: string,
    data: any,
    callback?: () => void,
  ) => Promise<void>;
  getDealerGroup: (dealerGroupId: string) => Promise<void>;
  getAllDealerGroups: (type: string) => Promise<void>;
  attachDealersToDealerGroup: (
    dealerGroupId: string,
    data: string[],
  ) => Promise<any>;
}

// ----------- Context Create -----------
export const DealerGroupContext = createContext<
  DealerGroupContextType | undefined
>(undefined);

// ----------- Provider Component -----------
export const DealerGroupProvider = ({ children }: { children: ReactNode }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [dealerGroups, setDealerGroups] = useState<any[]>([]);
  const [dealerGroup, setDealerGroup] = useState<any>({});
  const [selectedDealerGroup, setSelectedDealerGroup] = useState<any | null>(
    null,
  );

  const queryClient = useQueryClient();

  // ----------- API Calls -----------
  const addDealerGroup = async (dealerGroup: any, callback = () => {}) => {
    setLoading(true);
    setError('');
    try {
      const response = await DealerGroup.addDealerGroup(dealerGroup);
      // Check if response is successful (status 201 for creation)
      if (response.statusCode === 201) {
        // Clear error on success
        setError('');
        // Remove all dealer-groups related queries comprehensively
        queryClient.removeQueries({
          queryKey: ['GET', 'api/v2/dealer-groups'],
        });
        // This will remove all queries that start with the dealer-groups endpoint
        queryClient.removeQueries({
          predicate: query => {
            const queryKey = query.queryKey;
            return (
              Array.isArray(queryKey) &&
              queryKey.length >= 2 &&
              queryKey[0] === 'GET' &&
              queryKey[1]?.startsWith('api/v2/dealer-groups')
            );
          },
        });
        callback(); // Only call callback on success
      } else {
        setError(response.message || 'Failed to add dealer group');
      }
      // await getAllDealerGroups(dealerGroup.type);
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : 'Failed to add dealer group',
      );
    } finally {
      setLoading(false);
    }
  };

  const deleteDealerGroup = async (
    dealerGroupId: string,
    type: string,
    callback = () => {},
  ) => {
    setLoading(true);
    setError('');
    try {
      const response = await DealerGroup.deleteDealerGroup(dealerGroupId);
      // Check if response is successful (status 200)
      if (response.statusCode === 200) {
        // Clear error on success
        setError('');
        // Remove all dealer-groups related queries comprehensively
        queryClient.removeQueries({
          queryKey: ['GET', 'api/v2/dealer-groups'],
        });
        // This will remove all queries that start with the dealer-groups endpoint
        queryClient.removeQueries({
          predicate: query => {
            const queryKey = query.queryKey;
            return (
              Array.isArray(queryKey) &&
              queryKey.length >= 2 &&
              queryKey[0] === 'GET' &&
              queryKey[1]?.startsWith('api/v2/dealer-groups')
            );
          },
        });
        callback(); // Only call callback on success
      } else {
        setError(response.message || 'Failed to delete dealer group');
      }
      // await getAllDealerGroups(type);
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : 'Failed to delete dealer group',
      );
    } finally {
      setLoading(false);
    }
  };

  const editDealerGroup = async (
    dealerGroupId: string,
    data: any,
    callback = () => {},
  ) => {
    setLoading(true);
    setError('');
    try {
      const response = await DealerGroup.editDealerGroup(dealerGroupId, data);
      // Check if response is successful (status 200)
      if (response.statusCode === 200) {
        // Clear error on success
        setError('');
        // Remove all dealer-groups related queries comprehensively
        queryClient.removeQueries({
          queryKey: ['GET', 'api/v2/dealer-groups'],
        });
        // This will remove all queries that start with the dealer-groups endpoint
        queryClient.removeQueries({
          predicate: query => {
            const queryKey = query.queryKey;
            return (
              Array.isArray(queryKey) &&
              queryKey.length >= 2 &&
              queryKey[0] === 'GET' &&
              queryKey[1]?.startsWith('api/v2/dealer-groups')
            );
          },
        });
        callback(); // Only call callback on success
      } else {
        setError(response.message || 'Failed to edit dealer group');
      }
      // await getAllDealerGroups(data.type);
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : 'Failed to edit dealer group',
      );
    } finally {
      setLoading(false);
    }
  };

  const attachDealersToDealerGroup = async (
    dealerGroupId: string,
    data: any,
    callback = () => {},
  ) => {
    setLoading(true);
    setError('');
    try {
      const response = await DealerGroup.attachDealersToDealerGroup(
        dealerGroupId,
        data,
      );
      // Check if response is successful (status 200)
      if (response.statusCode === 200) {
        // Clear error on success
        setError('');
        // Remove all dealer-groups related queries comprehensively
        queryClient.removeQueries({
          queryKey: ['GET', 'api/v2/dealer-groups'],
        });
        // This will remove all queries that start with the dealer-groups endpoint
        queryClient.removeQueries({
          predicate: query => {
            const queryKey = query.queryKey;
            return (
              Array.isArray(queryKey) &&
              queryKey.length >= 2 &&
              queryKey[0] === 'GET' &&
              queryKey[1]?.startsWith('api/v2/dealer-groups')
            );
          },
        });
        callback(); // Only call callback on success
      } else {
        setError(
          response.message || 'Failed to attach dealers to dealer group',
        );
      }
      // await getAllDealerGroups(data.type);
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to attach dealers to dealer group',
      );
    } finally {
      setLoading(false);
    }
  };

  const getDealerGroup = async (dealerGroupId: string) => {
    setLoading(true);
    setError('');
    try {
      const response = await DealerGroup.getDealerGroup(dealerGroupId);
      setSelectedDealerGroup(response);
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to fetch dealer group details',
      );
    } finally {
      setLoading(false);
    }
  };

  const getAllDealerGroups = async (role: string) => {
    setLoading(true);
    setError('');
    try {
      const response = await DealerGroup.getAllDealerGroups(
        role ? { role } : {},
      );

      const sortedData = response.body.data.sort((a: any, b: any) =>
        a.name.localeCompare(b.name),
      );
      setDealerGroups(sortedData);
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : 'Failed to fetch dealer groups',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <DealerGroupContext.Provider
      value={{
        loading,
        error,
        dealerGroups,
        dealerGroup,
        selectedDealerGroup,
        setError,
        setDealerGroup,
        addDealerGroup,
        deleteDealerGroup,
        attachDealersToDealerGroup,
        editDealerGroup,
        getDealerGroup,
        getAllDealerGroups,
        isEditing,
        setIsEditing,
      }}
    >
      {children}
    </DealerGroupContext.Provider>
  );
};

// ----------- Custom Hook -----------
export const useDealerGroup = (): DealerGroupContextType => {
  const context = useContext(DealerGroupContext);
  if (context === undefined) {
    throw new Error('useDealerGroup must be used within a DealerGroupProvider');
  }
  return context;
};
